import { defineStore } from 'pinia';
import fetchOfferBySlugs from '~/lib/goliath/offer-by-slugs';
import { isAllowedToAddToCart } from '~/lib/magenta-moments';
import { groupBy } from 'lodash-es';

import type { BackendResponse } from '~/lib/shop-api';
import type { FetchError, MappedResponseType } from 'ofetch';
import { useCouponStore } from '~/stores/coupon';
import { useFeatures } from '~/composables/use-features';

// FetchError is not supported server side.
class BackendError extends Error {
  public data: any;
}

const raiseBackendErrors = (data: BackendResponse) => {
  if (data.errors && Object.keys(data.errors).length > 0) {
    const error = new BackendError('Backend error');
    error.data = data;
    throw error;
  }
};

export type AddToCart = { quantity?: number; articleNumber: string };
export type BundleProductItem = {
  quantity: number;
  product: OfferBundleProduct;
};
export type BundleProductInCart = {
  articleNumber: string;
  bundledProducts: BundleProductItem[];
};

export interface CartState {
  cart: Cart | null;
  initialized: boolean;
  csrfToken: string | undefined;
  bundleProducts: BundleProductInCart[];
  addToCartQueue: {
    articleNumber?: string;
    position: number;
  };
}

declare global {
  interface Window {
    testCustomerInQueue: number;
  }
}

let originalOperation: (() => Promise<MappedResponseType<'json', any>>) | null = null;

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

let simulateBeQueue: boolean | undefined;
/**
 * retry request until BE adds item to cart: https://i22team.atlassian.net/wiki/spaces/I22SHOPLIFTERS/pages/2945777778/FE-Implementation
 */
const retryPromise = (
  operation: () => Promise<MappedResponseType<'json', any>>,
  errorCallback: (position: number) => void
): Promise<MappedResponseType<'json', any>> =>
  new Promise((resolve, reject) => {
    // [START] simulate be queue - when Feature Flag is active!
    if (simulateBeQueue === undefined) {
      simulateBeQueue = useFeatures().features?.simulateBeQueue?.enabled;
      window.testCustomerInQueue = 5;
    }

    if (simulateBeQueue) {
      if (!originalOperation) originalOperation = operation;
      if (window.testCustomerInQueue || 0 > 1) {
        operation = () => {
          const e = new Error('BE heavy Load queue simulation') as FetchError;
          // The second request is a 503 and should be ignored
          e.response = {
            // @ts-ignore
            headers:
              window.testCustomerInQueue === 2
                ? {}
                : { 'x-customer-in-queue': String(window.testCustomerInQueue) },
            data: null,
            status: window.testCustomerInQueue === 2 ? 503 : 429,
            statusText:
              window.testCustomerInQueue === 2 ? 'Service Unavailable' : 'Too many customers',
            config: {},
          };
          window.testCustomerInQueue -= 1;
          return Promise.reject(e);
        };
      } else {
        operation = originalOperation;
      }
    }
    // [END] simulate be queue

    operation()
      .then((response) => resolve(response))
      .catch((error) => {
        const queue = parseInt(error?.response?.headers?.['x-customer-in-queue'], 10);
        // use queue when we get a 50X from the BE (eg. 503 Service Unavailable)
        if (queue > 0 || error?.response?.status > 500 || error?.response?.statusCode > 500) {
          errorCallback(queue);
          return wait(15000)
            .then(retryPromise.bind(this, operation, errorCallback))
            .then(resolve)
            .catch(reject);
        }
        errorCallback(0);
        return reject(error);
      });
  });

export const useCartStore = defineStore('cart', {
  state: (): CartState => ({
    cart: null,
    initialized: false,
    csrfToken: undefined,
    bundleProducts: [],
    addToCartQueue: {
      position: 0,
      articleNumber: undefined,
    },
  }),
  getters: {
    cartInitialized(state): boolean {
      return state.initialized;
    },
    hasTelekomProductInCart(state): boolean {
      return !!(state.cart && state.cart.items.some(({ provider }) => provider === 'TELEKOM'));
    },
    hasBrodosProductInCart(state): boolean {
      return !!(state.cart && state.cart.items.some(({ provider }) => provider === 'BRODOS'));
    },
    isMixedCart(): boolean {
      return this.hasTelekomProductInCart && this.hasBrodosProductInCart;
    },
    cartQuantity(state): number {
      return state.cart ? state.cart.items.reduce((total, item) => total + item.quantity, 0) : 0;
    },
    cartIsEmpty(): boolean {
      return this.cartQuantity === 0;
    },
    cartData(state): Cart | null {
      return state.cart;
    },
    cartItems(state): Item[] {
      return state.cart ? state.cart.items : [];
    },
    possiblePaymentTypes(state): string[] {
      return state.cart ? state.cart.possiblePaymentTypes : [];
    },
    paymentType(state): string | null {
      return state.cart?.paymentType || null;
    },
    cartState(state): string | undefined {
      return state.cart?.state;
    },
    paymentStatus(state): string | null {
      return state.cart ? state.cart.state : null;
    },
    cartAmountReductionsIncluded(state): number {
      return state.cart ? state.cart.amount : 0;
    },
    subTotal(state): number {
      return state.cart ? state.cart.subTotal : 0;
    },
    shippingCosts(state): number {
      const { cart } = state;
      if (!cart) return 0;
      const costs = [cart.shippingCostsTelekom, cart.shippingCostsBrodos];
      return costs.reduce((sum, curr) => (typeof curr === 'number' ? sum + curr : sum), 0);
    },
    totalPriceWithShipping(): number {
      return this.cartAmountReductionsIncluded + this.shippingCosts;
    },
    isFreeOfChargeCart(state): boolean {
      return !!(state.cart && state.cart.items.length > 0 && this.totalPriceWithShipping === 0);
    },
    couponCampaignName(state): string {
      return state.cart ? state.cart.redeemedCouponDescription || '' : '';
    },
    couponRedeemed(state): boolean {
      return !!(state.cart && (state.cart.redeemedCoupon?.length || 0) > 0);
    },
    redeemedCouponCode(state): string | undefined {
      return state.cart?.redeemedCoupon || undefined;
    },
    redeemedCouponType(state): string | undefined {
      return state.cart?.redeemedCouponType || undefined;
    },
    itemsVoucherReductionAmount(state): number {
      return state.cart
        ? state.cart.items
            .filter((item) => item.voucher && item.provider === 'TELEKOM')
            .reduce((sum, item) => sum + item.reductionAmount, 0)
        : 0;
    },
    voucherInitialSum(state): number {
      return state.cart ? state.cart.voucherInitialSum : 0;
    },
    voucherRestSum(state): number {
      return state.cart ? state.cart.voucherRestSum : 0;
    },
    calculatedVoucherReductionAmount(state): number {
      return (state.cart?.voucherReductionAmount || 0) + this.itemsVoucherReductionAmount;
    },
    getAnonymousCartData(state): any {
      if (!state.cart) return {};
      const validKeys = [
        'id',
        'orderKey',
        'state',
        'paymentType',
        'originalAmount',
        'amount',
        'redeemedCoupon',
        'redeemedCouponDescription',
        'shippingCostsTelekom',
        'shippingCostsBrodos',
        'possiblePaymentTypes',
        'jointCart',
        'paymentLinkTelekom',
        'paymentLinkBrodos',
        'paidTelekom',
        'paidBrodos',
        'branding',
        'voucherRestSum',
        'voucherReductionAmount',
        'redeemedCouponType',
        'voucherInitialSum',
        'gk',
        'paymentTypeRebateName',
        'paymentTypeRebateReductionAmount',
        'paymentTypeRebatePercentage',
        'paymentTypeRebateButtontext',
        'possiblePaymentTypeRebate',
        'paymentTypeRebateItems',
      ];
      const anonymousData = Object.fromEntries(
        Object.entries(state.cart).filter(([key]) => validKeys.includes(key))
      );
      anonymousData.items = state.cart?.items?.map(
        ({ name, quantity, articleNumber, price, coupon, voucher, rebateName }) => ({
          name,
          quantity,
          articleNumber,
          price,
          coupon,
          voucher,
          rebateName,
        })
      );
      anonymousData.itemsString = JSON.stringify(anonymousData.items);
      anonymousData.addressesAttributes = state.cart.addressesAttributes?.map((e) => ({
        category: e.category,
        id: e.id,
      }));
      return anonymousData;
    },
    addresses(state): any[] {
      return state.cart?.addressesAttributes || [];
    },
    wasPaypalExpressReceived(state): boolean {
      return state.cart?.state === 'paypal_express_address_received';
    },
    paymentTypeRebateName(state): string | undefined {
      return state.cart?.paymentTypeRebateName || undefined;
    },
    possiblePaymentTypeRebate(state): any {
      return state.cart?.possiblePaymentTypeRebate || undefined;
    },
    paymentTypeRebateReductionAmount(state): number | undefined {
      return state.cart?.paymentTypeRebateReductionAmount || undefined;
    },
    paymentTypeRebatePercentage(state): number | undefined {
      return state.cart?.paymentTypeRebatePercentage || undefined;
    },
    paymentTypeRebateButtontext(state): string | undefined {
      return state.cart?.paymentTypeRebateButtontext || undefined;
    },
    paymentTypeRebateItems(state): any[] {
      return state.cart?.paymentTypeRebateItems ?? [];
    },
    bundlesInCart(state): BundleProductInCart[] {
      return state.bundleProducts;
    },
  },
  actions: {
    async initCart() {
      if (this.initialized) return Promise.resolve(this.cart);
      return this.fetchCart(false);
    },
    async clearCart() {
      this.cart = null;
      await useShopApi().deleteOrder();
    },
    async fetchCart(showLoadingSpinner = true, throwError = false): Promise<boolean> {
      try {
        const data = await useShopApi().getCurrentCart(showLoadingSpinner);
        if (!data.order) return false;
        this.cart = data.order;
        this.initialized = true;

        const couponStore = useCouponStore();
        if (data.order.redeemedCoupon) {
          couponStore.checkCouponCode(data.order.redeemedCoupon).catch(() => {
            // When coupon is invalid, it is already removed from store and we can ignore the response!
          });
        } else {
          couponStore.clearCoupon();
        }
        const allBundleSlugs = data.order.items.filter(
          ({ bundleType }: Item) => bundleType === 'brodos'
        );
        await this.queryBrodosBundleSlugs(allBundleSlugs);
        return true;
      } catch (e) {
        if (throwError) throw e;
        console.error(e);
        return false;
      }
    },
    async updateCartItems(items: AddToCart[]) {
      const data = await useShopApi().items(items);
      if (data.order) {
        this.cart = data.order;
      }
      raiseBackendErrors(data);
      return data;
    },
    async addCartItems(items: AddToCart[]) {
      if (
        items.filter((item) => isAllowedToAddToCart(item.articleNumber)).length !== items.length
      ) {
        const error = new BackendError('Not allowed');
        error.data = {
          errors: {
            moments: [{ error: 'not_allowed' }],
          },
        };
        throw error;
      }
      return retryPromise(
        () => this.updateCartItems(items),
        (position) => {
          this.addToCartQueue.position = position;
          this.addToCartQueue.articleNumber =
            items.length === 1 ? items[0]?.articleNumber : undefined;
        }
      ).finally(() => {
        this.addToCartQueue.position = 0;
      });
    },
    async cartAddCoupon(redeemedCoupon: string) {
      const data = await useShopApi().addCouponToCart(redeemedCoupon);
      if (data.order) {
        this.cart = data.order;
        // If we have a coupon, store it to the coupon store.
        if (data.order.redeemedCoupon) {
          useCouponStore()
            .checkCouponCode(data.order.redeemedCoupon)
            .catch(() => {});
        }
      } else {
        await this.fetchCart();
      }
    },
    async updateCustomerData(order: Partial<Cart>) {
      const data = await useShopApi().updateCustomerData(order);
      if (!data.order) return;
      this.cart = data.order;
    },
    async queryBrodosBundleSlugs(bundleProducts: Item[]) {
      bundleProducts.forEach(async (bundleCartProduct) => {
        if (!bundleCartProduct.articleNumber || !bundleCartProduct.slug) return;
        if (
          this.bundleProducts.find(
            ({ articleNumber }: BundleProductInCart) =>
              articleNumber === bundleCartProduct.articleNumber
          )
        )
          return;
        try {
          const offer = await fetchOfferBySlugs({ productSlug: bundleCartProduct.slug });
          if (!offer || !offer.product) return;
          const bundleGoliathProduct = getBundleProduct(offer.product);
          if (!bundleGoliathProduct) return;
          const { articleNumber, bundledProducts } = bundleGoliathProduct;
          if (!articleNumber) return;
          const itemsGroupedByAN = groupBy(bundledProducts, 'articleNumber');
          const countedItems: BundleProductItem[] = Object.values(itemsGroupedByAN).map(
            (group) => ({
              product: group[0],
              quantity: group.length,
            })
          );
          this.bundleProducts.push({ articleNumber, bundledProducts: countedItems });
        } catch (err) {
          console.error(err);
        }
      });
    },
    async deleteCoupon() {
      try {
        await useShopApi().deleteCoupon();
        const couponStore = useCouponStore();

        couponStore.clearCoupon();
      } catch (e) {
        console.error(e);
      } finally {
        await this.fetchCart();
      }
    },
    setCsrfToken(token: string | undefined) {
      this.csrfToken = token;
    },
  },
});
